import { FC, useCallback, useMemo } from "react";
import { useBreakpoints } from "@otrium/core";
import { sanitize } from "isomorphic-dompurify";
import { theme } from "src/theme";
import { ProductGender } from "src/types/graphql.d";
import { Carousel } from "src/organisms/Carousel";
import { ProductCardWishlist } from "src/organisms/ProductCardWishlist";
import { ProductPhoto } from "src/molecules/ProductPhoto";
import { Skeleton } from "src/atoms/Skeleton";
import {
  StyledCardBrand,
  StyledCardPrices,
} from "src/molecules/ProductCard/ProductCard.styled";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import useAddedToCartSuggestions from "src/hooks/useAddedToCartSuggestions";
import { trackCarouselViewedEvent } from "src/tracking/events/carousel-viewed";
import { trackCarouselItemSelectedEvent } from "src/tracking/events/carousel-item-selected";
import {
  CAROUSEL_CONTENT_TYPE,
  POPUP_SIMILAR_ITEMS_CAROUSEL_ID,
  POPUP_SIMILAR_ITEMS_LIMIT,
} from "src/utils/constants";
import { GATED_ADDED_TO_CART_POPUP } from "src/constants/gatedItem";
import { getRecommendationsPodAttributes } from "src/utils/constructor-beacon";
import {
  SuggestionsCarousel,
  SuggestionsTitle,
  SuggestionsWrapper,
} from "./AddedToCart.styled";

interface Props {
  productId: string;
  productSlug: string;
}

const SuggestionCardSkeleton: FC = () => (
  <>
    <ProductPhoto>
      <Skeleton />
    </ProductPhoto>
    <StyledCardBrand isPlaceholder>
      <Skeleton />
    </StyledCardBrand>
    <StyledCardPrices isPlaceholder>
      <Skeleton />
    </StyledCardPrices>
  </>
);

const AddedToCartSuggestions: FC<Props> = ({ productId, productSlug }) => {
  const { isMobile } = useBreakpoints();
  const { currentShopType } = useShopTypeContext();
  const shopType = (currentShopType as ProductGender) || ProductGender.Women;

  const { title, products, loading, podId, resultId, strategyId } =
    useAddedToCartSuggestions({
      productId,
      productSlug,
      shopType,
    });

  const trackingData = useMemo(() => {
    if (!title || !products.length) {
      return;
    }

    return {
      title,
      items: products,
      carouselId: POPUP_SIMILAR_ITEMS_CAROUSEL_ID,
      carouselPosition: 1,
      contentType: CAROUSEL_CONTENT_TYPE.PRODUCT,
      shopType,
    };
  }, [title, products, shopType]);

  const handleCarouselViewed = useCallback(
    (visibleIndexes: number[]) => {
      if (trackingData) {
        void trackCarouselViewedEvent({ ...trackingData, visibleIndexes });
      }
    },
    [trackingData]
  );

  const handleProductClick = useCallback(
    (itemId: string) => {
      if (trackingData) {
        void trackCarouselItemSelectedEvent({ ...trackingData, itemId });
      }
    },
    [trackingData]
  );

  const isLoading = loading && !products.length;

  const cardTrackingData = {
    shopType,
    pageName: GATED_ADDED_TO_CART_POPUP,
    carouselType: POPUP_SIMILAR_ITEMS_CAROUSEL_ID,
  };

  const items = isLoading
    ? Array.from({ length: POPUP_SIMILAR_ITEMS_LIMIT }, (_, index) => (
        <SuggestionCardSkeleton key={`skeleton-${index}`} />
      ))
    : products.map((product, index) => (
        <ProductCardWishlist
          key={product.id}
          position={index}
          product={product}
          onProductClickTracker={handleProductClick}
          showLabels={false}
          showName={false}
          showSizes={false}
          zoomImageOnHover={false}
          trackingData={cardTrackingData}
          cnstrcRecommendation={Boolean(podId)}
          cnstrcSection="Products"
          cnstrcStrategyId={strategyId}
        />
      ));

  if (!isLoading && !products.length) {
    return null;
  }

  return (
    <SuggestionsWrapper
      data-testid={POPUP_SIMILAR_ITEMS_CAROUSEL_ID}
      {...getRecommendationsPodAttributes({
        podId,
        resultId,
        seedItems: productId,
        numResults: products.length,
      })}
    >
      {isLoading ? (
        <SuggestionsTitle as="div">
          <Skeleton width={160} />
        </SuggestionsTitle>
      ) : (
        <SuggestionsTitle
          dangerouslySetInnerHTML={{ __html: sanitize(title ?? "") }}
        />
      )}
      <SuggestionsCarousel>
        <Carousel
          items={items}
          itemsPerView={{ mobile: 2, tablet: 3, desktop: 3 }}
          gap={theme.space.space8}
          parentPadding={theme.space.space16}
          fullWidthMobile={isMobile}
          trackCarouselViewed={isLoading ? undefined : handleCarouselViewed}
        />
      </SuggestionsCarousel>
    </SuggestionsWrapper>
  );
};

export default AddedToCartSuggestions;
